import { ApeironBlueButton, NextImage, SkeletonBox } from '@apeiron/temp'
import { ApeironTooltip } from '@apeiron/ui'
import { Box, Button, styled, Typography } from '@mui/material'
import InfoSVG from '@public/icons/icon-info.svg'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, ReactNode, useCallback, useMemo } from 'react'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const UpperBox = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;

  min-height: 22px;
`

const Title = styled(Typography)`
  color: #c4c4c4;
  font-size: 16px;
  font-weight: 300;
  line-height: 16px;
  padding-left: 3px;
`

const InfoButton = styled(Button)`
  padding: unset;
  min-width: unset;
`

const InfoImage = styled(NextImage)`
  width: 13px;
  aspect-ratio: 1;
`

const StyledApeironTooltip = styled(ApeironTooltip)`
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 20px !important;
  min-width: 320px;
`

const Textfield = styled(SkeletonBox)<TextfieldProps>`
  background-color: #151615;
  border-radius: 2px;
  padding: 6px 10px;
  min-height: 20px;
  width: 100%;
  & {
    font-size: 16px;
    font-weight: 400;
    line-height: 16px;
  }
  &.Custom-Typography-Skeleton {
    padding: unset;
    background-color: unset;
  }
`

const SubTextfield = styled(Box)`
  margin-left: 2px;
  display: inline;
`

const LinkButton = styled(ApeironBlueButton)`
  font-size: 14px;
  border-radius: 4px;

  height: 22px;
`

const ApeironLabelTextfield: FC<Props> = (props: Props) => {
  const {
    children,
    className,
    emptyOverlay,
    explain,
    loading,
    subText,
    text,
    title,
    linkButton,
    infoButtonPopup
  } = props

  const handleOnRenderContent = useCallback(() => {
    if (!R.isNil(children)) {
      return R.isNil(emptyOverlay) ? children : emptyOverlay
    }

    return (
      <Textfield className='Custom-Textfield' loading={loading}>
        {R.isNil(emptyOverlay) ? (
          <>
            {text}
            {RA.isNotNilOrEmpty(subText) ? (
              <SubTextfield className='Custom-Sub-Textfield'>
                {subText}
              </SubTextfield>
            ) : (
              <></>
            )}
          </>
        ) : (
          <>{emptyOverlay}</>
        )}
      </Textfield>
    )
  }, [children, emptyOverlay, loading, subText, text])

  const handleOnClickInfo = useCallback(() => {
    infoButtonPopup?.onInfoClick()
  }, [infoButtonPopup])

  const tooltipId = useMemo(() => {
    const prepareId = R.pipe(R.split(' '), R.join(''), R.toLower)

    return title ? prepareId(title) : ''
  }, [title])

  const contentExist = RA.isNotNilOrEmpty(text) || RA.isNotNilOrEmpty(children)

  const show = !R.isNil(loading) ? loading || contentExist : contentExist

  const renderInfoButton =
    RA.isNotNilOrEmpty(infoButtonPopup) || RA.isNotNilOrEmpty(explain)

  return show ? (
    <Container className={className}>
      {RA.isNotNilOrEmpty(title) && (
        <UpperBox>
          <Title className='Custom-Title'>{title}</Title>
          {renderInfoButton && (
            <>
              <InfoButton
                data-tooltip-id={tooltipId}
                onClick={handleOnClickInfo}
              >
                <InfoImage src={InfoSVG} />
              </InfoButton>
              <StyledApeironTooltip id={tooltipId}>
                {explain}
              </StyledApeironTooltip>
            </>
          )}
          {infoButtonPopup?.popup}
          {linkButton && (
            <LinkButton onClick={linkButton?.onLinkClick}>
              {linkButton?.name}
            </LinkButton>
          )}
        </UpperBox>
      )}
      {handleOnRenderContent()}
    </Container>
  ) : (
    <></>
  )
}

type TextfieldProps = {
  empty?: boolean
}

type Props = {
  children?: ReactNode
  className?: string
  emptyOverlay?: ReactNode
  explain?: string
  loading?: boolean
  subText?: string | undefined
  text?: ReactNode
  title?: string
  linkButton?: {
    name: string
    onLinkClick: () => void
  }
  infoButtonPopup?: {
    popup: ReactNode
    onInfoClick: () => void
  }
}

export default ApeironLabelTextfield
